import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import NewsletterSignup from '../home/components/NewsletterSignup';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const eventsSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Lotus Maternal Community Events — Brooklyn, NY',
  description: 'Community events for mothers and families hosted by Lotus Maternal, including walks, workouts, and playdates in Brooklyn, NY.',
  url: `${siteUrl}/events`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Events', item: `${siteUrl}/events` },
    ],
  },
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Event',
        name: 'Mommy Mondays — Child Birth Education Class',
        startDate: '2026-03-30T13:00:00',
        endDate: '2026-03-30T15:00:00',
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        location: {
          '@type': 'Place',
          name: 'Lotus Maternal',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'New York',
            addressRegion: 'NY',
            addressCountry: 'US',
          },
        },
        description: 'A structured doula-led childbirth education class that prepares mothers for pregnancy, birth, and postpartum. Hosted by Doula Brie.',
        url: 'https://posh.vip/e/mommy-mondays',
        organizer: {
          '@type': 'MedicalBusiness',
          name: 'Lotus Maternal',
          url: siteUrl,
          telephone: '(929)473-8867',
        },
        image: 'https://storage.readdy-site.link/project_files/da95ef90-efae-477a-aecc-96807964cd22/163eb42b-6b38-4078-81bf-476affd2d130_Pink-and-White-Modern-Pilates-Class-Instagram-Post.png?v=0074304d7b71c99c1848802eef475c08',
      },
    },
  ],
};

const events = [
  {
    id: 2,
    title: 'Mommy Mondays',
    subtitle: 'Child Birth Education Class',
    date: 'March 30, 2026',
    dateShort: '03.30',
    time: '1:00 PM',
    location: 'Lotus Maternal',
    address: 'New York, NY',
    category: 'Childbirth Education',
    description:
      'A structured doula-led childbirth education class that prepares mothers for pregnancy, birth, and postpartum. Hosted by Doula Brie, this class gives you the knowledge, confidence, and support to navigate every stage of your journey into motherhood.',
    highlights: [
      'Progressive pregnancy and birth techniques',
      '1:1 Doula Support with Doula Brie',
      'Postpartum preparation and recovery guidance',
      'Hands-on learning in a supportive group setting',
    ],
    image: 'https://storage.readdy-site.link/project_files/da95ef90-efae-477a-aecc-96807964cd22/163eb42b-6b38-4078-81bf-476affd2d130_Pink-and-White-Modern-Pilates-Class-Instagram-Post.png?v=0074304d7b71c99c1848802eef475c08',
    instagram: '@lotus.maternal',
    website: 'www.lotusmaternal.com',
    color: 'pink',
    tag: 'RSVP Required',
    tagColor: 'bg-pink-600',
    rsvpLink: 'https://posh.vip/e/mommy-mondays',
    rsvpExternal: true,
    rsvpLabel: 'RSVP on Posh',
    featured: true,
  },
];

const EventsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Community Events for Mothers | Lotus Maternal Brooklyn NY"
        description="Join Lotus Maternal community events in Brooklyn, NY. Mommy Mondays Childbirth Education Class with Doula Brie. Mommy & Me Monday Mornings at Prospect Park."
        keywords="mothers community events Brooklyn, mommy mondays childbirth education, doula brie class, postpartum doula events, maternal wellness events New York"
        canonical={`${siteUrl}/events`}
        ogTitle="Community Events for Mothers | Lotus Maternal Brooklyn NY"
        ogDescription="Join Lotus Maternal community events in Brooklyn, NY. Childbirth education classes and community gatherings for moms."
        schema={eventsSchema}
      />

      {/* Hero Section */}
      <section className="relative h-[55vh] min-h-[460px] flex items-center justify-center overflow-hidden mt-24">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e6440b294d4b8fc6526c09d691e6c966.jpg"
            alt="Lotus Maternal Events"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 space-y-4 w-full">
          <span className="inline-block bg-pink-500/90 text-white text-sm font-semibold px-5 py-1.5 rounded-full mb-2 tracking-wide">
            Community &amp; Events
          </span>
          <h1 className="font-playfair text-5xl lg:text-7xl font-bold drop-shadow-lg">
            Upcoming Events
          </h1>
          <p className="text-xl lg:text-2xl max-w-2xl mx-auto text-white/90">
            Gather, move, and grow together with the Lotus Maternal community
          </p>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20 bg-gradient-to-b from-pink-50/60 to-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-pink-500 font-semibold tracking-widest text-sm uppercase mb-2">
              Mark Your Calendar
            </p>
            <h2 className="font-playfair text-4xl font-bold text-gray-900">
              What&apos;s Coming Up
            </h2>
          </div>

          {events.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-3xl overflow-hidden mb-12 border border-pink-100"
            >
              {event.featured ? (
                /* Featured event — flyer layout */
                <div className="grid lg:grid-cols-[420px_1fr]">
                  {/* Flyer image column */}
                  <div className="relative bg-pink-50 flex items-center justify-center p-8 min-h-[520px]">
                    <div className={`absolute top-5 right-5 ${event.tagColor} text-white text-xs font-bold px-4 py-1.5 rounded-full z-10`}>
                      {event.tag}
                    </div>
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full max-w-[340px] h-auto object-contain rounded-2xl"
                    />
                  </div>

                  {/* Info column */}
                  <div className="p-10 lg:p-14 flex flex-col justify-between">
                    <div>
                      <span className="inline-block bg-pink-100 text-pink-600 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">
                        {event.category}
                      </span>
                      <h3 className="font-playfair text-3xl lg:text-4xl font-bold text-gray-900 mb-1 leading-tight">
                        {event.title}
                      </h3>
                      {event.subtitle && (
                        <p className="font-playfair text-xl text-pink-500 font-semibold mb-5">{event.subtitle}</p>
                      )}

                      <div className="flex flex-wrap gap-4 mb-6 mt-4">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <div className="w-7 h-7 flex items-center justify-center bg-pink-50 rounded-full">
                            <i className="ri-calendar-line text-pink-500"></i>
                          </div>
                          <span className="font-medium">{event.date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <div className="w-7 h-7 flex items-center justify-center bg-pink-50 rounded-full">
                            <i className="ri-time-line text-pink-500"></i>
                          </div>
                          <span className="font-medium">{event.time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <div className="w-7 h-7 flex items-center justify-center bg-pink-50 rounded-full">
                            <i className="ri-map-pin-line text-pink-500"></i>
                          </div>
                          <span className="font-medium">{event.address}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <div className="w-7 h-7 flex items-center justify-center bg-pink-50 rounded-full">
                            <i className="ri-phone-line text-pink-500"></i>
                          </div>
                          <a href="tel:9294738867" className="font-medium hover:text-pink-500 transition-colors">(929) 473-8867</a>
                        </div>
                      </div>

                      <p className="text-gray-600 text-base leading-relaxed mb-6">
                        {event.description}
                      </p>

                      <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Class Includes:</p>
                      <div className="space-y-2.5 mb-8">
                        {event.highlights.map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-checkbox-circle-fill text-lg text-pink-400"></i>
                            </div>
                            <span className="text-gray-700 text-sm font-medium">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <a
                          href="https://instagram.com/lotus.maternal"
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="flex items-center gap-1.5 hover:text-pink-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-instagram-line text-base"></i>
                          {event.instagram}
                        </a>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-global-line text-base"></i>
                          {event.website}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <a
                          href={event.rsvpLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-pink-500 hover:bg-pink-600 text-white px-10 py-3.5 rounded-full font-bold text-sm transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-ticket-2-fill text-sm"></i>
                          </div>
                          {event.rsvpLabel}
                        </a>
                        <a
                          href="https://instagram.com/lotus.maternal"
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="border-2 border-pink-200 text-pink-500 hover:bg-pink-50 px-8 py-3 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Follow for Updates
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Standard event layout */
                <div className="grid lg:grid-cols-2">
                  <div className="relative h-[420px] lg:h-auto min-h-[420px]">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-6 left-6 bg-white rounded-2xl px-5 py-4 text-center min-w-[90px]">
                      <div className="text-pink-500 font-black text-4xl leading-none">
                        {event.dateShort.split('.')[1]}
                      </div>
                      <div className="text-gray-500 text-xs font-semibold uppercase tracking-widest mt-1">
                        {new Date(event.date).toLocaleString('default', { month: 'short' })}
                      </div>
                    </div>
                    <div className={`absolute top-6 right-6 ${event.tagColor} text-white text-xs font-bold px-4 py-1.5 rounded-full`}>
                      {event.tag}
                    </div>
                  </div>

                  <div className="p-10 lg:p-14 flex flex-col justify-between">
                    <div>
                      <span className="inline-block bg-pink-100 text-pink-600 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 tracking-wide uppercase">
                        {event.category}
                      </span>
                      <h3 className="font-playfair text-3xl lg:text-4xl font-bold text-gray-900 mb-1 leading-tight">
                        {event.title}
                      </h3>
                      {event.subtitle && (
                        <p className="font-playfair text-xl text-pink-500 font-semibold mb-4">{event.subtitle}</p>
                      )}
                      <div className="flex flex-wrap gap-4 mb-6 mt-4">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <div className="w-7 h-7 flex items-center justify-center bg-pink-50 rounded-full">
                            <i className="ri-calendar-line text-pink-500"></i>
                          </div>
                          <span className="font-medium">{event.date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <div className="w-7 h-7 flex items-center justify-center bg-pink-50 rounded-full">
                            <i className="ri-time-line text-pink-500"></i>
                          </div>
                          <span className="font-medium">{event.time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <div className="w-7 h-7 flex items-center justify-center bg-pink-50 rounded-full">
                            <i className="ri-map-pin-line text-pink-500"></i>
                          </div>
                          <span className="font-medium">{event.address}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 text-base leading-relaxed mb-6">
                        {event.description}
                      </p>
                      <div className="space-y-2.5 mb-8">
                        {event.highlights.map((item, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-checkbox-circle-fill text-lg text-emerald-500"></i>
                            </div>
                            <span className="text-gray-700 text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <a
                          href="https://instagram.com/lotus.maternal"
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="flex items-center gap-1.5 hover:text-pink-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-instagram-line text-base"></i>
                          {event.instagram}
                        </a>
                        <span className="flex items-center gap-1.5">
                          <i className="ri-global-line text-base"></i>
                          {event.website}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <Link
                          to={event.rsvpLink}
                          className="bg-pink-500 hover:bg-pink-600 text-white px-8 py-3 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
                        >
                          {event.rsvpLabel}
                        </Link>
                        <a
                          href="https://instagram.com/lotus.maternal"
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="border-2 border-pink-200 text-pink-500 hover:bg-pink-50 px-8 py-3 rounded-full font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Follow for Updates
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="text-center mt-6">
            <div className="inline-flex flex-col items-center gap-3 bg-gradient-to-br from-pink-50 to-emerald-50 border border-pink-100 rounded-3xl px-12 py-10">
              <div className="w-14 h-14 flex items-center justify-center bg-pink-100 rounded-full">
                <i className="ri-calendar-2-line text-2xl text-pink-500"></i>
              </div>
              <h4 className="font-playfair text-2xl font-bold text-gray-900">More Events Coming Soon</h4>
              <p className="text-gray-500 text-sm max-w-sm">
                Follow us on Instagram <strong>@lotus.maternal</strong> or subscribe to our newsletter to be the first to know about new events.
              </p>
              <a
                href="https://instagram.com/lotus.maternal"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="mt-2 bg-gradient-to-r from-pink-500 to-emerald-500 text-white px-8 py-3 rounded-full font-semibold text-sm hover:opacity-90 transition-all whitespace-nowrap cursor-pointer"
              >
                Follow @lotus.maternal
              </a>
            </div>
          </div>
        </div>
      </section>

      <NewsletterSignup />

      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20African%20American%20mothers%20laughing%20together%20outdoors%20in%20a%20park%20sunny%20day%20community%20bonding%20strollers%20babies%20warm%20joyful%20atmosphere%20soft%20bokeh%20background%20pink%20and%20green%20tones&width=1920&height=600&seq=eventscta001&orientation=landscape"
            alt="Join our community"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-pink-900/80 to-emerald-900/80"></div>
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center text-white space-y-6">
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold">Join Our Village</h2>
          <p className="text-xl text-white/90 leading-relaxed">
            You don&apos;t have to do this alone. Our community of Black mothers is here to walk alongside you — literally and figuratively.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link
              to="/booking"
              className="bg-white text-pink-600 px-10 py-4 rounded-full font-semibold text-lg hover:bg-pink-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Book a Consultation
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-pink-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;